import { Copy, Eye, EyeOff, KeyRound, Lock, ShieldAlert, UserPlus, X } from "lucide-react"
import * as React from "react"

import { PageHeader } from "@/components/layout/page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Notice } from "@/components/ui/notice"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { InfoTip } from "@/components/ui/tooltip"
import { ADMIN_ROLE, ApiError, createPerson, listPeople, updatePerson } from "@/lib/api"
import type { CreatedPerson, PeopleList, Person } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import { EMPTY, formatDate, formatTime } from "@/lib/format"
import { AccountPanel } from "@/pages/settings/account"
import { SettingsTabs } from "@/pages/settings/tabs"

/**
 * Settings → People: кто работает в аккаунте (AMEND-8 §3 шаг 7).
 *
 * Единственный экран консоли, который **заводит** людей, поэтому он же —
 * единственное место, где открытый пароль вообще появляется на экране. Из этого
 * всё его устройство: пароль показывается скрытым, раскрывается по явному
 * нажатию, живёт только в памяти вкладки и пропадает вместе с панелью. Ни в
 * адрес, ни в `localStorage`, ни в состояние списка он не попадает — AMEND-8 §5
 * запрещает ему оказываться где-либо ещё, включая скриншот приёмки, который
 * снимают с этого самого экрана.
 *
 * Сверху на той же странице — имя аккаунта (`./account.tsx`, шаг 8). Два раздела
 * рядом не случайно: оба закрыты одним и тем же заслоном (админ аккаунта) и оба
 * отвечают на вопрос «чей это аккаунт» — один именем, другой списком людей.
 *
 * Раздел отдан `Settings`, а не новому пункту меню: меню повторяет консоль SEON
 * пункт в пункт (`components/layout/nav.ts`), и придуманный пункт «People»
 * ломал бы это соответствие ради одной таблицы.
 *
 * Не админ видит не пустую страницу и не 403 в консоли браузера, а объяснение:
 * ручки отвечают 403 намеренно (человек вошёл, мы его знаем, но управление
 * людьми — работа админа аккаунта), и экран обязан это проговорить.
 */
export function PeoplePage() {
  const { identity } = useAuth()
  const [data, setData] = React.useState<PeopleList | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<ApiError | Error | null>(null)
  const [created, setCreated] = React.useState<CreatedPerson | null>(null)

  const load = React.useCallback((signal?: AbortSignal) => {
    setLoading(true)
    return listPeople(signal)
      .then((result) => {
        setData(result)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (signal?.aborted) return
        setData(null)
        setError(cause instanceof Error ? cause : new Error("Request failed"))
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false)
      })
  }, [])

  React.useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  const forbidden = error instanceof ApiError && error.status === 403

  return (
    <>
      <PageHeader title="Settings" tabs={<SettingsTabs />} />
      <div className="p-5">
        <div className="mx-auto max-w-4xl space-y-4">
          {forbidden && <NotAnAdmin message={error.message} />}

          {!forbidden && error && (
            <section className="animate-panel-in rounded border border-line bg-surface p-6 text-center shadow-panel">
              <p className="text-sm font-medium text-decline">{error.message}</p>
              <p className="mx-auto mt-1 max-w-md text-xs text-fg-muted">
                The list stays empty on purpose — an empty table would read as “this account has
                no people”, which is a different thing entirely.
              </p>
              <Button className="mt-4" onClick={() => void load()}>
                Retry
              </Button>
            </section>
          )}

          {!error && (
            <>
              <AccountPanel
                onRenamed={(name) =>
                  // Заголовок списка людей берёт имя из ответа `/api/people`, и
                  // после правки оно бы врало до следующей загрузки страницы.
                  // Перезапрашивать список ради одной строки незачем: сюда
                  // приехало то, что легло в базу.
                  setData((current) => (current ? { ...current, account_name: name } : current))
                }
              />

              <InvitePanel
                busyList={loading}
                onCreated={(result) => {
                  setCreated(result)
                  void load()
                }}
              />

              {created && <PasswordPanel created={created} onClose={() => setCreated(null)} />}

              <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
                <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-3">
                  <div>
                    <span className="stencil-label text-fg-muted">People</span>
                    <h2 className="mt-0.5 text-sm font-semibold text-fg">
                      {data ? data.account_name : "Account"}
                    </h2>
                  </div>
                  <span className="text-xs text-fg-subtle">
                    Everyone here works inside this account only.
                  </span>
                </header>

                {loading && !data ? (
                  <ListSkeleton />
                ) : (
                  <PeopleTable
                    people={data?.items ?? []}
                    selfId={identity?.user_id ?? null}
                    onChanged={(person) =>
                      setData((current) =>
                        current
                          ? {
                              ...current,
                              items: current.items.map((row) =>
                                row.id === person.id ? person : row,
                              ),
                            }
                          : current,
                      )
                    }
                  />
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </>
  )
}

/** Вошедший — не админ. Объясняем, а не показываем пустоту. */
function NotAnAdmin({ message }: { message: string }) {
  return (
    <section className="animate-panel-in rounded border border-line bg-surface p-6 shadow-panel">
      <div className="flex items-center gap-2 text-fg-muted">
        <Lock className="h-4 w-4" strokeWidth={1.75} />
        <span className="stencil-label">Admins only</span>
      </div>
      <p className="mt-3 text-sm leading-relaxed text-fg">{message}</p>
      <p className="mt-2 text-xs leading-relaxed text-fg-muted">
        Your account works normally — this one page is closed. Ask an admin of your account to
        add or disable people for you.
      </p>
    </section>
  )
}

/** Заведение человека: почта, роль и ничего больше. Аккаунт берётся из сессии. */
function InvitePanel({
  busyList,
  onCreated,
}: {
  busyList: boolean
  onCreated: (created: CreatedPerson) => void
}) {
  const [email, setEmail] = React.useState("")
  const [role, setRole] = React.useState("user")
  const [error, setError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await createPerson(email.trim(), role)
      setEmail("")
      setRole("user")
      onCreated(result)
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : "Could not reach the API.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="animate-panel-in rounded border border-line bg-surface shadow-panel">
      <header className="border-b border-line px-4 py-3">
        <span className="stencil-label text-fg-muted">Add a person</span>
        <p className="mt-1 text-xs leading-relaxed text-fg-muted">
          There is no self sign-up and no invite mail in this build. The server generates a
          password and shows it to you once — hand it over yourself.
        </p>
      </header>
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2 px-4 py-3">
        <div className="min-w-[14rem] flex-1">
          <label htmlFor="new-person-email" className="stencil-label mb-1.5 block text-fg-muted">
            Work email
          </label>
          <Input
            id="new-person-email"
            type="email"
            value={email}
            required
            disabled={busy || busyList}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="name@company.com"
          />
        </div>
        <div>
          <span className="stencil-label mb-1.5 block text-fg-muted">Role</span>
          <Select value={role} onValueChange={setRole} disabled={busy || busyList}>
            <SelectTrigger className="w-32" aria-label="Role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="user">User</SelectItem>
              <SelectItem value={ADMIN_ROLE}>Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" variant="primary" disabled={busy || busyList}>
          <UserPlus className="h-3.5 w-3.5" strokeWidth={2.25} />
          {busy ? "Creating…" : "Create"}
        </Button>
        {error && (
          <Notice tone="error" className="w-full">
            {error}
          </Notice>
        )}
      </form>
    </section>
  )
}

/**
 * Пароль заведённого человека — первый и последний раз.
 *
 * Закрыт по умолчанию: панель висит поверх рабочего экрана, и «показать» —
 * осознанное действие, а не состояние, в котором админ случайно снимет
 * скриншот или отойдёт от монитора. Копирование не раскрывает значение: в
 * буфер уходит то же, что в поле, а глазами его видеть для этого не нужно.
 */
function PasswordPanel({
  created,
  onClose,
}: {
  created: CreatedPerson
  onClose: () => void
}) {
  const [shown, setShown] = React.useState(false)
  const [copied, setCopied] = React.useState<"idle" | "done" | "failed">("idle")

  async function copy() {
    try {
      await navigator.clipboard.writeText(created.password)
      setCopied("done")
    } catch {
      // Буфер обмена недоступен без защищённого соединения и без разрешения.
      // Молчать здесь нельзя: админ решит, что скопировал, закроет панель и
      // останется без пароля.
      setCopied("failed")
    }
  }

  return (
    <section className="animate-panel-in rounded border border-review/50 bg-surface shadow-raised">
      <header className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-review" strokeWidth={2} />
          <div>
            <span className="stencil-label text-fg">One-time password</span>
            <p className="mt-0.5 text-xs text-fg-muted">
              for <span className="text-fg">{created.person.email}</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss password"
          className="flex h-7 w-7 items-center justify-center rounded text-fg-muted hover:bg-surface-sunken hover:text-fg"
        >
          <X className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </header>

      <div className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <output
            className="min-w-[16rem] flex-1 rounded border border-line-strong bg-surface-sunken px-2.5 py-2 font-mono text-xs text-fg"
            aria-label="Generated password"
          >
            {shown ? created.password : "•".repeat(32)}
          </output>
          <Button type="button" onClick={() => setShown((value) => !value)}>
            {shown ? (
              <EyeOff className="h-3.5 w-3.5" strokeWidth={2} />
            ) : (
              <Eye className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            {shown ? "Hide" : "Reveal"}
          </Button>
          <Button type="button" onClick={() => void copy()}>
            <Copy className="h-3.5 w-3.5" strokeWidth={2} />
            {copied === "done" ? "Copied" : "Copy"}
          </Button>
        </div>

        <Notice tone="warn" className="mt-3">
          This is the only time this password is shown — we keep a hash, not the password. If it
          is lost, create the person again or reset it from the server. On first sign-in they
          will be asked to set up an authenticator app.
        </Notice>

        {copied === "failed" && (
          <Notice tone="error" className="mt-2">
            The browser refused clipboard access. Reveal the password and copy it by hand.
          </Notice>
        )}
      </div>
    </section>
  )
}

function PeopleTable({
  people,
  selfId,
  onChanged,
}: {
  people: Person[]
  selfId: number | null
  onChanged: (person: Person) => void
}) {
  const [busyId, setBusyId] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  async function patch(id: number, values: { is_active?: boolean; role?: string }) {
    setBusyId(id)
    setError(null)
    try {
      onChanged(await updatePerson(id, values))
    } catch (cause: unknown) {
      setError(cause instanceof ApiError ? cause.message : "Could not reach the API.")
    } finally {
      setBusyId(null)
    }
  }

  if (people.length === 0) {
    return (
      <p className="px-4 py-8 text-center text-xs text-fg-muted">
        No people in this account yet.
      </p>
    )
  }

  return (
    <>
      {error && (
        <Notice tone="error" className="mx-4 mt-3">
          {error}
        </Notice>
      )}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>2FA</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Last sign-in</TableHead>
              <TableHead align="right">Access</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.map((person) => {
              const isSelf = person.id === selfId
              const busy = busyId === person.id
              return (
                <TableRow key={person.id} className="animate-row-in">
                  <TableCell>
                    <span className={person.is_active ? undefined : "text-fg-muted line-through"}>
                      {person.email}
                    </span>
                    {isSelf && <span className="ml-2 text-xs text-fg-subtle">you</span>}
                  </TableCell>

                  <TableCell>
                    {isSelf ? (
                      // Свою роль менять нельзя (ручка отвечает 409): админ,
                      // разжаловавший себя, закрывает управление людьми всему
                      // аккаунту. Показываем причину, а не мёртвый селект.
                      <InfoTip label="You cannot change your own role">
                        <span className="inline-flex">
                          <Badge tone={person.role === ADMIN_ROLE ? "accent" : "neutral"}>
                            {person.role}
                          </Badge>
                        </span>
                      </InfoTip>
                    ) : (
                      <Select
                        value={person.role}
                        disabled={busy}
                        onValueChange={(value) => void patch(person.id, { role: value })}
                      >
                        <SelectTrigger
                          className="h-7 w-[6.5rem] text-xs"
                          aria-label={`Role of ${person.email}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">user</SelectItem>
                          <SelectItem value={ADMIN_ROLE}>admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>

                  <TableCell>
                    {person.totp_enabled ? (
                      <Badge tone="approve">on</Badge>
                    ) : (
                      <InfoTip label="Authenticator app not set up yet — it is set up on first sign-in">
                        <span className="inline-flex">
                          <Badge tone="review">pending</Badge>
                        </span>
                      </InfoTip>
                    )}
                  </TableCell>

                  <TableCell className="whitespace-nowrap text-fg-muted">
                    <span>{formatDate(person.created_at)}</span>
                  </TableCell>

                  <TableCell className="whitespace-nowrap text-fg-muted">
                    {person.last_login_at ? (
                      <span>
                        {formatDate(person.last_login_at)}{" "}
                        <span className="text-fg-subtle">{formatTime(person.last_login_at)}</span>
                      </span>
                    ) : (
                      <InfoTip label="Access handed out, never used">
                        <span className="text-fg-subtle">{EMPTY}</span>
                      </InfoTip>
                    )}
                  </TableCell>

                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-xs text-fg-muted">
                        {person.is_active ? "active" : "disabled"}
                      </span>
                      {isSelf ? (
                        <InfoTip label="You cannot disable your own access">
                          <span className="inline-flex">
                            <Switch checked disabled aria-label="Your own access" />
                          </span>
                        </InfoTip>
                      ) : (
                        <Switch
                          checked={person.is_active}
                          disabled={busy}
                          aria-label={`Access for ${person.email}`}
                          onCheckedChange={(value) =>
                            void patch(person.id, { is_active: value })
                          }
                        />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <p className="flex items-start gap-2 border-t border-line px-4 py-3 text-xs leading-relaxed text-fg-muted">
        <ShieldAlert className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
        <span>
          Disabling someone closes the door both ways: their open session stops working on the
          next request, and their password stops opening a new one. Nothing they did is deleted.
        </span>
      </p>
    </>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className="h-7 w-full" />
      ))}
    </div>
  )
}
